import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { useLocation } from 'react-router-dom';
import { BookOpen, MessageCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ChatContainer from '../components/chat/ChatContainer';
import RightPanes, { PANES_WIDTH, PANES_GAP } from '../components/chat/panes/RightPanes';
import SourcesPane from '../components/chat/panes/SourcesPane';
import AssistantPane from '../components/chat/panes/AssistantPane';
import { useChatSession } from '../contexts/ChatSessionContext';
import { ChatMessageResponse, Citation } from '../services/chatbot.service';
import { useAssistant } from '../features/assistant/useAssistant';
import { useFeedbackTrigger } from '../features/assistant/useFeedbackTrigger';
import { assistantStorage } from '../features/assistant/assistantStorage';

function ChatPage() {
  const location = useLocation();
  const initialQuery = new URLSearchParams(location.search).get('q') || undefined;
  const chatSession = useChatSession();
  const currentSessionId = chatSession?.currentSessionId ?? null;
  const titleOf = (id: string | null) => (id ? chatSession?.sessions.find(s => s.id === id)?.title : null) || null;
  const title = titleOf(currentSessionId);

  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sourceList, setSourceList] = useState<Citation[]>([]);
  const [activeDoc, setActiveDoc] = useState<string | null>(null);
  const [turn, setTurn] = useState<{ sessionId: string; message: ChatMessageResponse } | null>(null);
  const [streaming, setStreaming] = useState(false);
  const typingRef = useRef(false);
  const assistantBtnRef = useRef<HTMLButtonElement>(null);
  const sourcesBtnRef = useRef<HTMLButtonElement>(null);
  const assistant = useAssistant();

  // Conversación vigente para validar disparos que llegan 20 s después.
  const currentSessionRef = useRef<string | null>(currentSessionId);
  currentSessionRef.current = currentSessionId;

  useFeedbackTrigger({
    turn, isStreaming: streaming, userTypingRef: typingRef,
    // Si el usuario ya abrió el Asistente sobre esta respuesta, el disparo no la reinicia.
    onTrigger: t => {
      if (t.sessionId !== currentSessionRef.current || t.messageId === assistant.target?.messageId) return;
      assistant.startFor(t, 'auto');
    },
  });

  // Al cambiar de conversación, el Asistente y el turno pendiente no se arrastran a la nueva.
  const assistantTarget = assistant.target;
  const assistantReset = assistant.reset;
  useEffect(() => {
    if (assistantTarget && assistantTarget.sessionId !== currentSessionId) assistantReset();
    setTurn(t => (t && t.sessionId !== currentSessionId ? null : t));
    // Las fuentes pertenecen a la conversación anterior.
    setSourceList([]); setActiveDoc(null); setSourcesOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  // Referencia estable: ChatBubble memoiza sus citas con esta función.
  const handleDocumentClick = useCallback((citation: Citation, messageCitations: Citation[]) => {
    setSourceList(messageCitations.length ? messageCitations : [citation]);
    setActiveDoc(citation.document_id);
    setSourcesOpen(true);
  }, []);

  const handleDraftChange = useCallback((has: boolean) => { typingRef.current = has; }, []);
  // Un envío nuevo descarta el turno anterior: se cancela su espera de 20 s.
  const handleStreamingChange = useCallback((s: boolean) => {
    setStreaming(s);
    if (s) setTurn(null);
  }, []);

  const closeSources = () => { setSourcesOpen(false); sourcesBtnRef.current?.focus(); };
  const closeAssistant = () => { assistant.setOpen(false); assistantBtnRef.current?.focus(); };

  const openAssistantManually = () => {
    if (assistant.open) { assistant.setOpen(false); return; }
    const idle = ['asking', 'registered', 'dismissed'].includes(assistant.state.phase);
    if (turn && turn.sessionId === currentSessionId && turn.message.id !== assistant.target?.messageId && idle) {
      assistant.startFor({ sessionId: turn.sessionId, messageId: turn.message.id }, 'manual');
      // Ya se atendió esta conversación: el disparo automático no vuelve a preguntar.
      assistantStorage.markAsked(turn.sessionId);
    }
    assistant.setOpen(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const inSources = (document.activeElement as HTMLElement | null)?.closest('[aria-label="Fuentes"]');
      if (sourcesOpen && (inSources || !assistant.open)) { closeSources(); return; }
      if (assistant.open) closeAssistant();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesOpen, assistant]);

  const anyOpen = sourcesOpen || assistant.open;
  const iconBtn = (on: boolean) => `relative w-9 h-9 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 disabled:hover:bg-transparent ${on ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`;

  const actions = (
    <>
      <button ref={sourcesBtnRef} className={iconBtn(sourcesOpen)} aria-label="Fuentes" title="Fuentes" disabled={!sourceList.length}
        onClick={() => setSourcesOpen(o => !o)}>
        <BookOpen className="w-5 h-5" />
      </button>
      <button ref={assistantBtnRef} className={iconBtn(assistant.open)} title="Asistente" onClick={openAssistantManually}
        aria-label={assistant.dot ? 'Asistente, pregunta pendiente' : 'Asistente'}>
        <MessageCircle className="w-5 h-5" />
        {assistant.dot && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-indigo-600 ring-2 ring-white" aria-hidden="true" />}
      </button>
    </>
  );

  // Mientras la conversación no tenga título, la cabecera dice dónde estás (DESIGN.md §11.1 y §12).
  return (
    <Layout title={title || 'Nueva conversación'} actions={actions} rightInset={anyOpen ? PANES_WIDTH + PANES_GAP * 2 : 0}>
      <div className="h-[calc(100vh-85px)] -m-4 lg:-m-8">
        <ChatContainer
          onDocumentClick={handleDocumentClick}
          initialQuery={initialQuery}
          onTurnSettled={setTurn}
          onDraftChange={handleDraftChange}
          onStreamingChange={handleStreamingChange}
        />
      </div>
      <AnimatePresence>
      {anyOpen && (
        <RightPanes key="panel-derecho">
          {sourcesOpen && (
            <SourcesPane citations={sourceList} activeDocumentId={activeDoc}
              onSelect={c => setActiveDoc(c.document_id)} onClose={closeSources} />
          )}
          {assistant.open && (
            <AssistantPane state={assistant.state} hasTarget={assistant.hasTarget} optedOut={assistant.optedOut} disabled={assistant.disabled} topic={titleOf(assistant.target?.sessionId ?? null)} error={assistant.error}
              onRate={assistant.rate} onAnswer={assistant.answer} onConfirm={assistant.confirm} onCorrect={assistant.correct}
              onSnooze={assistant.snooze} onOptOut={assistant.optOut} onOptIn={assistant.optIn} onComment={assistant.comment}
              onClose={closeAssistant} />
          )}
        </RightPanes>
      )}
      </AnimatePresence>
    </Layout>
  );
}

// ChatSessionProvider vive en App.tsx: la barra lateral y el chat comparten la lista en todas las pestañas.
export default function BusquedaInteligente() {
  return <ChatPage />;
}
